import {
  ComponentObject,
  ComponentSelectors,
  ComponentElements,
  ComponentInjector,
  UIComponent,
  ComponentService,
} from "@atypon/ui-utils";

class Texts extends ComponentObject {
  doi = "";
  apiUrl = "";
  isCrossrefEnabled = "";
  getFTRContent = "";
  getFTRButton = "";
  getFTRLink = "";
  prevItemDoi = "";
  requestSource = "";
}

class ClassList extends ComponentObject {
  getFTRPlaceholder = "getFTR__placeholder";
}

class Selectors extends ComponentSelectors {
  article = "article";
  articleReferences = "article .article__references";
  articleCitedBy = "article .citedBySection";
  tabContent = ".tab--slide .tab__content";
  tabReferences = "#pane-pcw-references";
  tabRelated = "#pane-pcw-related";
  getFTRPlaceholder = ".getFTR__placeholder";
  wrapperItem = ""; // Reference or citedBy item
}

class Elements extends ComponentElements<Selectors> {
  article: HTMLElement;
  articleReferences: HTMLElement;
  articleCitedBy: HTMLElement;
  tabContent: HTMLElement;
  tabReferences: HTMLElement;
  tabRelated: HTMLElement;
  getFTRWrapper: HTMLElement;
  getFTRPlaceholder: HTMLElement;
  source: HTMLElement; // References tab, related tab, article references or article citedBy
}

interface GetFTR
  extends ComponentService<Selectors, Elements, ClassList, Texts> { }

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class GetFTR implements UIComponent {
  isButtonVisible: boolean = false;
  linksCount: number;
  numberOfPlaceholders: number;
  isSafeToFetch: boolean = true;

  initialize(): void {
    this.setElements();
    this.addEventListeners();
    this.getGetFTRConfig();
  }

  private setElements(): void {
    this.elements.articleReferences = this.domUtils.getElement<HTMLElement>(
      this.selectors.articleReferences,
    );
    this.elements.articleCitedBy = this.domUtils.getElement<HTMLElement>(
      this.selectors.articleCitedBy,
    );
    this.elements.tabContent = this.domUtils.getElement<HTMLElement>(
      this.selectors.tabContent,
    );
  }

  addEventListeners(): void {
    window.addEventListener("scroll", this.handleWindowScroll);
  }

  private getGetFTRConfig(): void {
    this.elements.article = this.domUtils.getElement<HTMLElement>(
      this.selectors.article,
    );
    this.texts.isCrossrefEnabled = this.elements.article.getAttribute(
      "data-crossref-enabled",
    );
    this.texts.requestSource = this.elements.article.getAttribute(
      "data-getFTR-request-source",
    );
    this.texts.doi = this.elements.article.getAttribute(
      "data-getFTR-doi",
    );
    this.linksCount = parseInt(
      this.elements.article.getAttribute("data-getFTR-links-count"),
    );
  }

  private getGetFTRElements(): void {
    const placeholderElements = this.domUtils
      .getElements(this.selectors.getFTRPlaceholder, this.elements.source)
      .slice(0, this.linksCount);
    const firstPlaceholderElement =
      placeholderElements && placeholderElements[0];
    const firstItemDoi = firstPlaceholderElement
      ?.closest("[data-doi]")
      ?.getAttribute("data-doi");
    const elementsData = placeholderElements
      ?.map(
        (element) =>
          element.closest(this.selectors.wrapperItem)?.getAttribute("data-doi"),
      )
      .filter(Boolean); // to remove any null or undefined values from the array

    if (
      elementsData?.length > 0 &&
      this.numberOfPlaceholders &&
      firstItemDoi !== this.texts.prevItemDoi
    ) {
      this.texts.prevItemDoi = firstItemDoi;
      this.fetchData(elementsData);
      this.elements.tabContent?.addEventListener("scroll", () => {
        this.handleTabScroll(this.elements.source);
      });
    }
  }

  private setGetFTRSource(
    sourceElement: HTMLElement,
    wrapperItemSelector: string,
    apiUrl: string,
  ): void {
    this.initialize();
    this.elements.source = sourceElement;
    this.selectors.wrapperItem = wrapperItemSelector;
    this.numberOfPlaceholders =
      this.elements.source?.querySelectorAll<HTMLElement>(
        this.selectors.getFTRPlaceholder,
      ).length;
    this.texts.apiUrl = this.elements.article.getAttribute(apiUrl);
    this.getGetFTRElements();
  }

  referencesTab(): void {
    this.elements.tabReferences = this.domUtils.getElement<HTMLElement>(
      this.selectors.tabReferences,
    );
    this.setGetFTRSource(
      this.elements.tabReferences,
      ".references__item",
      "data-getFTR-reference-url",
    );
  }

  relatedTab(): void {
    this.elements.tabRelated = this.domUtils.getElement<HTMLElement>(
      this.selectors.tabRelated,
    );
    this.setGetFTRSource(
      this.elements.tabRelated,
      ".citedByEntry",
      "data-getFTR-citedby-url",
    );
  }

  articleReferences(): void {
    this.setGetFTRSource(
      this.elements.articleReferences,
      ".references__item",
      "data-getFTR-reference-url",
    );
  }

  articleCitedBy(): void {
    this.setGetFTRSource(
      this.elements.articleCitedBy,
      ".citedByEntry",
      "data-getFTR-citedBy-url",
    );
  }

  private handleTabScroll(source: HTMLElement): void {
    const firstPlaceholder = source?.querySelector<HTMLElement>(
      this.selectors.getFTRPlaceholder,
    );
    const firstPlaceholderOffsetTop = firstPlaceholder?.offsetTop;
    const tabContentScrollY = this.elements.tabContent?.scrollTop;

    if (
      firstPlaceholder &&
      firstPlaceholderOffsetTop - 500 < tabContentScrollY &&
      this.isSafeToFetch
    ) {
      this.getGetFTRElements();
    }
  }

  private handleWindowScroll = (): void => {
    if (!this.elements.articleReferences && !this.elements.articleCitedBy) {
      return;
    }

    const scrollPosition = window.scrollY;
    const articleCitedByOffsetTop = this.elements.articleCitedBy?.offsetTop;
    const targetElement =
      scrollPosition >= articleCitedByOffsetTop
        ? this.elements.articleCitedBy
        : this.elements.articleReferences;

    const firstPlaceholder = targetElement?.querySelector<HTMLElement>(
      this.selectors.getFTRPlaceholder,
    );
    const firstPlaceholderOffsetTop = firstPlaceholder?.offsetTop;

    if (
      firstPlaceholder &&
      this.isSafeToFetch &&
      firstPlaceholderOffsetTop - 500 < scrollPosition
    ) {
      if (targetElement === this.elements.articleReferences) {
        this.articleReferences();
      } else {
        this.articleCitedBy();
      }
    }
  };

  private fetchData(elementsData: string[]): void {
    const requestBody = {
      pubDoi: this.texts.doi,
      associatedCitations: elementsData,
      requestSource: this.texts.requestSource,
    };

    this.isSafeToFetch = false;

    fetch(this.texts.apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((results) => {
        results.forEach((item: object) => {
          this.elements.getFTRWrapper = this.elements.source?.querySelector(
            `[data-doi="${item["doi"]}"]`,
          ); // Reference or cited by element
          this.elements.getFTRPlaceholder =
            this.elements.getFTRWrapper?.querySelector(
              this.selectors.getFTRPlaceholder,
            );
          this.isEntitlement(item);
          this.getFTRContentEmpty();
          this.removePlaceholder();
        });
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        this.isSafeToFetch = true;
      });
  }

  private isEntitlement = (item: object): void => {
    if (item["entitlement"]) {
      this.isButtonVisible = true;
      this.getFTRButton(item["entitlement"]);
    } else if (
      !this.texts.isCrossrefEnabled &&
      item["doi"] &&
      item["fallback"]
    ) {
      this.isButtonVisible = true;
      this.getFTRButton(item["fallback"]["url"]);
    } else if (this.texts.isCrossrefEnabled && item["fallback"]) {
      this.getFTRLink(item["fallback"]);
    }
  };

  private getFTRButton = (data: object | string): void => {
    let isIconVisible = false;
    let iconName = "";
    let iconEl = "";
    const isEntitlement = typeof data === "object";
    const btnText = isEntitlement ? data["label"] : "View";
    const btnLink = isEntitlement ? data["url"] : data;
    const tooltip = isEntitlement && data["tooltip"] ? data["tooltip"] : false;

    if (
      isEntitlement &&
      (data["entitled"] === "yes" ||
        (data["entitled"] === "maybe" && data["type"] === "vor"))
    ) {
      isIconVisible = true;
      iconName = "icon-getFTR-na";
    } else if (
      isEntitlement &&
      data["entitled"] === "no" &&
      data["type"] === "av"
    ) {
      isIconVisible = true;
      iconName = "icon-getFTR-av";
    }

    iconEl = isIconVisible
      ? `<i aria-hidden="true" class="${iconName}"></i>`
      : "";
    this.texts.getFTRButton = `<a href="${btnLink}"  aria-label="${tooltip ? tooltip : btnText
      }" target="_blank" data-tooltip="${tooltip}" class="getFTR__btn ${tooltip ? "getFTR__tooltip" : ""
      }"  tabindex="0">
               ${iconEl}
               ${btnText}
            </a>`;

    this.elements.getFTRPlaceholder?.insertAdjacentHTML(
      "afterbegin",
      this.texts.getFTRButton,
    );
  };

  private getFTRLink = (data: object): void => {
    this.texts.getFTRLink = `<a href="${data["url"]}"  target="_blank" class="getFTR__link">
               ${data["label"]}
            </a>`;

    this.elements.getFTRPlaceholder?.insertAdjacentHTML(
      "afterbegin",
      this.texts.getFTRLink,
    );
  };

  private getFTRContentEmpty(): void {
    this.texts.getFTRButton = "";
    this.texts.getFTRLink = "";
    this.isButtonVisible = false;
  }

  private removePlaceholder(): void {
    this.elements.getFTRPlaceholder?.classList.remove("getFTR__placeholder");
  }
}

export default GetFTR;

export {
  Elements as GetFTRElements,
  Selectors as GetFTRSelectors,
  ClassList as GetFTRClassList,
  Texts as GetFTRTexts,
};
